import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { firestore } from '../services/firebase';
import type { Account } from '../types/account';
import type { Procurement } from '../types/procurement';
import { getRecentProcurementIds } from '../utils/recentProcurements';

export const useRecentProcurements = (account: Account | null) => {
  const [recentProcurements, setRecentProcurements] = useState<Procurement[]>([]);

  useEffect(() => {
    if (!account?.uid || !account?.clientId) {
      setRecentProcurements([]);
      return;
    }

    const fetchProcurements = async () => {
      const ids = getRecentProcurementIds(account.uid);
      if (ids.length === 0) {
        setRecentProcurements([]);
        return;
      }

      const fetched: Procurement[] = [];
      for (const id of ids) {
        try {
          const docRef = doc(firestore, 'procurements', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            // Ensure the user actually has access to this procurement
            if (data.clientId === account.clientId && data.archived !== true) {
              fetched.push({ id: docSnap.id, ...data } as Procurement);
            }
          }
        } catch (_e) {
          // Ignore individual fetch errors
        }
      }
      setRecentProcurements(fetched);
    };

    fetchProcurements();

    const handleUpdate = () => {
      fetchProcurements();
    };

    window.addEventListener('recentProcurementsUpdated', handleUpdate);
    return () => window.removeEventListener('recentProcurementsUpdated', handleUpdate);
  }, [account?.uid, account?.clientId]);

  return recentProcurements;
};
